import { ReactNode, useEffect, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { CartDrawer } from '@/components/cart/CartDrawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, MessageCircle } from 'lucide-react';
import { MasterCartIconButton } from '@/components/cart/MasterCart';
import { MobileBottomNav } from '@/components/MobileBottomNav';
import { NotificationsBell } from '@/components/NotificationsBell';
import { LegalFooter } from '@/components/LegalFooter';
import { supabase } from '@/integrations/supabase/client';

const roleTitles: Record<string, string> = {
  brand: 'Sis Thoko',
  spaza: 'Sis Thoko',
  community: 'Sis Thoko',
  logistics: 'Sis Thoko',
  admin: 'Sis Thoko · Admin',
  data_capturer: 'Sis Thoko · Data',
};

export function AppLayout({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const { itemCount, setIsOpen } = useCart();
  const role = profile?.role || 'spaza';
  const showLegacyCart = role === 'admin';

  const [fp, setFp] = useState<{ business_type: string | null; business_name: string | null; location: string | null; whatsapp_opt_in: boolean | null } | null>(null);
  useEffect(() => {
    if (!profile?.id) return;
    supabase.from('foodpreneur_profiles')
      .select('business_type,business_name,location,whatsapp_opt_in')
      .eq('user_id', profile.id).maybeSingle()
      .then(({ data }) => setFp(data as any));
  }, [profile?.id]);

  const firstName = (fp?.business_name || profile?.full_name || profile?.email?.split('@')[0] || 'there').split(' ')[0];
  const businessLabel = fp?.business_type === 'baker' ? 'Baker' : fp?.business_type === 'caterer' ? 'Caterer' : fp?.business_type === 'food_store' ? 'Food Store' : '';
  const waActive = !!fp?.whatsapp_opt_in;
  const isFoodpreneur = role !== 'admin' && role !== 'data_capturer';

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile top bar */}
          {isFoodpreneur && (
            <header className="md:hidden sticky top-0 z-20 bg-card/95 backdrop-blur border-b px-4 py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground leading-tight truncate">Hi {firstName}</p>
                <p className="text-[11px] text-muted-foreground leading-tight truncate">
                  {[businessLabel, fp?.location].filter(Boolean).join(' · ') || 'Foodpreneur'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" title={waActive ? 'WhatsApp sync active' : 'WhatsApp sync off'}>
                  <MessageCircle className="h-4 w-4" />
                  <span className={`h-2 w-2 rounded-full ${waActive ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                </span>
                <NotificationsBell />
                <MasterCartIconButton />
              </div>
            </header>
          )}

          {/* Desktop / tablet header */}
          <header className="hidden md:flex h-14 items-center border-b bg-card px-4 gap-3 shrink-0">
            <SidebarTrigger />
            <div className="h-5 w-px bg-border" />
            <span className="text-sm font-medium text-foreground">{roleTitles[role] || 'Portal'}</span>
            <div className="ml-auto flex items-center gap-3">
              {showLegacyCart && (
                <Button variant="ghost" size="sm" className="relative" onClick={() => setIsOpen(true)}>
                  <ShoppingCart className="h-5 w-5" />
                  {itemCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-[10px] bg-primary text-primary-foreground">
                      {itemCount}
                    </Badge>
                  )}
                </Button>
              )}
              {isFoodpreneur && (
                <span className="hidden lg:inline-flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded-full px-2.5 py-1">
                  <MessageCircle className="h-3.5 w-3.5" />
                  WhatsApp
                  <span className={`h-2 w-2 rounded-full ${waActive ? 'bg-green-500' : 'bg-muted-foreground/40'}`} />
                </span>
              )}
              <NotificationsBell />
              <MasterCartIconButton />
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-muted-foreground">Live Market Data</span>
            </div>
          </header>

          <main className="flex-1 p-4 sm:p-6 overflow-auto pb-24 md:pb-6">
            {children}
          </main>
          <LegalFooter />
        </div>
      </div>
      {isFoodpreneur && <MobileBottomNav />}
      <CartDrawer />
    </SidebarProvider>
  );
}
